import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Inbox, AlertTriangle, Phone, MapPin, Calendar, Share2, Copy, Check, Pencil, X, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { LOGO_URL, formShareUrl, shareWhatsApp } from "@/lib/brand";
import { STATUS_META, STATUS_ORDER, DRAFT_META, STATUS_OPTIONS } from "@/lib/formConfig";
import { toast } from "@/components/ui/sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LeadRecap } from "@/components/lead/LeadRecap";
import { LeadEditFields } from "@/components/lead/LeadEditFields";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.nouveau;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${m.bg} ${m.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function RowBadge({ lead }) {
  if (lead.completion === "draft") {
    const m = DRAFT_META[lead.draft_state] || DRAFT_META.abandonne;
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${m.bg} ${m.text}`} data-testid={`suivi-draft-badge-${lead.draft_state}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${m.dot} ${lead.draft_state === "en_cours" ? "animate-pulse" : ""}`} />
        {m.label}
      </span>
    );
  }
  return <StatusBadge status={lead.status} />;
}

function ShareCard({ refCode }) {
  const [copied, setCopied] = useState(false);
  const link = formShareUrl(refCode);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-primary/20 bg-primary/[0.04] p-5" data-testid="suivi-share-card">
      <p className="flex items-center gap-2 font-heading text-base font-bold text-foreground">
        <Share2 className="h-4 w-4 text-primary" /> Partagez votre lien de formulaire
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Chaque demande envoyée via ce lien personnel vous est automatiquement attribuée.
      </p>
      <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
        <p className="min-w-0 flex-1 truncate text-xs text-slate-600" data-testid="suivi-share-link">{link}</p>
        <button
          type="button"
          onClick={copy}
          data-testid="suivi-copy-link"
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copié" : "Copier"}
        </button>
      </div>
      <button
        type="button"
        onClick={() => shareWhatsApp(refCode)}
        data-testid="suivi-whatsapp-share"
        className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-whatsapp px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-whatsapp-dark"
      >
        <Share2 className="h-4 w-4" /> Partager via WhatsApp
      </button>
    </div>
  );
}

function SuiviLeadSheet({ token, leadId, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    if (!leadId) { setLead(null); setEditing(false); return; }
    let active = true;
    api.get(`/suivi/${token}/lead/${leadId}`)
      .then((r) => { if (active) { setLead(r.data); setEditing(false); } })
      .catch(() => { if (active) toast.error("Impossible de charger la fiche."); });
    return () => { active = false; };
  }, [token, leadId]);

  const patch = (p) => setEditData((d) => ({ ...d, ...p }));
  const startEdit = () => { setEditData({ ...lead }); setEditing(true); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/suivi/${token}/lead/${leadId}`, { fields: editData });
      setLead(res.data);
      setEditing(false);
      toast.success("Fiche mise à jour");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    setSavingStatus(true);
    try {
      const res = await api.patch(`/suivi/${token}/lead/${leadId}/status`, { status });
      setLead(res.data);
      toast.success("Statut mis à jour");
      onChanged?.();
    } catch {
      toast.error("Échec de la mise à jour");
    } finally {
      setSavingStatus(false);
    }
  };

  const isDraft = lead?.completion === "draft";

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="suivi-lead-sheet">
        {!lead ? (
          <>
            <SheetHeader className="sr-only"><SheetTitle>Chargement de la fiche</SheetTitle></SheetHeader>
            <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          </>
        ) : (
          <>
            <SheetHeader className="space-y-3 pb-2">
              <SheetTitle className="font-heading text-2xl">{lead.prenom} {lead.nom}</SheetTitle>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <RowBadge lead={lead} />
                <Select
                  value={STATUS_OPTIONS.some((o) => o.value === lead.status) ? lead.status : undefined}
                  onValueChange={changeStatus}
                  disabled={savingStatus}
                >
                  <SelectTrigger className="h-9 w-44" data-testid="suivi-status-trigger">
                    <SelectValue placeholder={isDraft ? "Qualifier…" : "Statut"} />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`suivi-status-option-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isDraft && (
                <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="suivi-draft-notice">
                  Fiche non finalisée par le client. Choisir un statut (ex. « En attente ») le convertit en lead à traiter.
                </p>
              )}
              <div className="flex items-center gap-2">
                {!editing ? (
                  <button
                    type="button"
                    data-testid="suivi-edit-button"
                    onClick={startEdit}
                    className="flex h-9 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <Pencil className="h-4 w-4" /> Modifier la fiche
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="suivi-save-button"
                      onClick={save}
                      disabled={saving}
                      className="flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
                    </button>
                    <button
                      type="button"
                      data-testid="suivi-cancel-button"
                      onClick={() => setEditing(false)}
                      className="flex h-9 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <X className="h-4 w-4" /> Annuler
                    </button>
                  </>
                )}
              </div>
            </SheetHeader>

            <div className="space-y-1 py-3">
              {editing ? (
                <LeadEditFields data={editData} set={patch} />
              ) : (
                <LeadRecap lead={lead} fileUrlFor={(p) => `/suivi/${token}/file/${p}`} />
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function SuiviLicencie() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/suivi/${code}`);
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-6 text-center" data-testid="suivi-invalid">
        <img src={LOGO_URL} alt="Place des Énergies" className="h-12 w-auto" />
        <AlertTriangle className="mt-4 h-10 w-10 text-rose-400" />
        <h1 className="font-heading text-xl font-bold text-foreground">Lien de suivi invalide</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          Ce lien n'est pas valide ou a expiré. Contactez votre conseillère Place des Énergies pour obtenir votre lien de suivi.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pde-grid-bg">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3 sm:px-8">
          <img src={LOGO_URL} alt="Place des Énergies" className="h-12 w-auto" />
          <span className="hidden text-sm font-semibold text-slate-400 sm:inline">| Suivi de mes leads</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">Espace apporteur</p>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground sm:text-3xl" data-testid="suivi-licensee-name">
            {data.licensee_name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Suivez en temps réel les leads que vous avez transmis et leur avancement.
          </p>
        </motion.div>

        {data.ref_code && <ShareCard refCode={data.ref_code} />}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total envoyé</p>
            <p className="mt-1 font-heading text-3xl font-bold text-foreground">{data.total}</p>
          </div>
          {STATUS_ORDER.map((s) => (
            <div key={s} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{STATUS_META[s].label}</p>
              <p className={`mt-1 font-heading text-3xl font-bold ${STATUS_META[s].text}`}>{data.stats?.[s] ?? 0}</p>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Non finalisés</p>
            <p className="mt-1 font-heading text-3xl font-bold text-sky-600">{data.brouillon ?? 0}</p>
          </div>
        </div>

        {/* List */}
        <div className="mt-6">
          {data.leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="suivi-empty">
              <Inbox className="h-10 w-10 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-600">Aucun lead pour le moment</p>
              <p className="text-sm text-muted-foreground">Partagez votre lien de formulaire à vos clients pour commencer.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
                <div className="col-span-4">Client</div>
                <div className="col-span-3">Localisation</div>
                <div className="col-span-3">Reçu le</div>
                <div className="col-span-2 text-right">Statut</div>
              </div>
              <ul className="divide-y divide-slate-100">
                {data.leads.map((lead) => (
                  <li key={lead.id}>
                    <button
                      type="button"
                      data-testid={`suivi-lead-${lead.id}`}
                      onClick={() => setSelectedId(lead.id)}
                      className="grid w-full grid-cols-1 gap-1 px-5 py-4 text-left transition-colors hover:bg-slate-50 sm:grid-cols-12 sm:items-center sm:gap-3"
                    >
                      <div className="col-span-4 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-semibold text-foreground">{lead.prenom} {lead.nom}</p>
                          {lead.demande_urgente === "Oui" && (
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
                              <AlertTriangle className="h-3 w-3" /> Urgent
                            </span>
                          )}
                        </div>
                        <p className="flex items-center gap-1 truncate text-sm text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" /> {lead.telephone_portable}
                        </p>
                      </div>
                      <div className="col-span-3 flex items-center gap-1 truncate text-sm text-slate-600">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" /> {lead.code_postal_ville || "—"}
                      </div>
                      <div className="col-span-3 flex items-center gap-1 text-sm text-slate-500">
                        <Calendar className="h-3.5 w-3.5 text-slate-400" /> {fmtDate(lead.created_at)}
                      </div>
                      <div className="col-span-2 mt-1 flex items-center gap-1 sm:mt-0 sm:justify-end">
                        <RowBadge lead={lead} />
                        <ChevronRight className="hidden h-4 w-4 text-slate-300 sm:block" />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Place des Énergies · Ce lien est personnel, ne le partagez pas.
        </p>
      </main>

      <SuiviLeadSheet
        token={code}
        leadId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}
